import React, { useState, useEffect, useContext } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  Tab,
  Tabs,
} from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import Select from "react-select";
import NumberFormat from "react-number-format";
import { IconUser, IconCheck } from "@tabler/icons-react";
import {
  createCustomer,
  getCustomer,
  listCustomerTags,
  updateCustomer,
} from "../services/CustomerServices";
import NotificationsContext from "../../../contexts/NotificationsContext";
import {
  prepareCustomerData,
  searchCEP,
  validateCNPJ,
  validateCPF,
} from "../utils";
import { formatPhoneForAPI, formatPhoneNumber } from "../../../helpers/Helpers";

const CustomerModal = ({ show, onHide, customer, isEdit, onSave }) => {
  const { setNotify } = useContext(NotificationsContext);
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [activeTab, setActiveTab] = useState("basic");

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
    clearErrors,
  } = useForm({
    shouldFocusError: false,
    defaultValues: {
      person_type: "PF",
      status: "active",
      customer_type: "new",
      gender: "N",
      communication_preference: "whatsapp",
      accepts_marketing: true,
      is_whatsapp_active: true,
    },
  });

  const defaultFormValues = {
    // Dados básicos
    full_name: "",
    nickname: "",
    person_type: "PF",
    cpf: "",
    cnpj: "",
    rg: "",
    phone_primary: "",
    phone_secondary: "",
    email: "",
    birth_date: "",
    gender: "N",

    // Endereço
    zip_code: "",
    address: "",
    address_number: "",
    address_complement: "",
    neighborhood: "",
    city: "",
    state: "",

    // Informações comerciais
    status: "active",
    customer_type: "new",
    acquisition_channel: "",
    referral_source: "",
    communication_preference: "whatsapp",
    preferred_schedule: "",
    marital_status: "",
    notes: "",

    // Tags
    tags_list: [], // Para o select múltiplo de tags

    // Contato de emergência
    emergency_contact_name: "",
    emergency_contact_phone: "",

    // Configurações
    is_whatsapp_active: true,
    accepts_marketing: true,

    // Campos que não são editáveis mas podem estar no form
    metadata: {},
  };

  const personType = watch("person_type");
  const zipCode = watch("zip_code");

  useEffect(() => {
    console.log(show);
    if (show) {
      console.log(customer);
      fetchTags();
      if (isEdit && customer) {
        console.log(111111111);
        populateForm(customer);
      } else {
        reset(defaultFormValues);
      }
    }
  }, [show, isEdit, customer, reset]);

  // Buscar CEP automaticamente quando digitado
  useEffect(() => {
    if (zipCode && zipCode.replace(/\D/g, "").length === 8) {
      handleCEPSearch(zipCode);
    }
  }, [zipCode]);

  const fetchTags = async () => {
    try {
      const response = await listCustomerTags();
      const tagOptions =
        response.data.results?.map((tag) => ({
          value: tag.id,
          label: tag.name,
          color: tag.color,
        })) || [];
      setTags(tagOptions);
    } catch (error) {
      console.error("Erro ao buscar tags:", error);
    }
  };

  const populateForm = (customerData) => {
    let data = {
      ...customerData,
      phone_primary: formatPhoneNumber(customerData?.phone_primary),
      phone_secondary: formatPhoneNumber(customerData?.phone_secondary),
      emergency_contact_phone: formatPhoneNumber(
        customerData?.emergency_contact_phone
      ),
    };
    const formData = { ...data };

    // Converter tags se existirem
    if (customerData.tags && customerData.tags.length > 0) {
      const tagList = customerData.tags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
      formData.tags_list = tagList;
    }

    reset(formData);
  };

  const handleCEPSearch = async (cep) => {
    setCepLoading(true);
    try {
      const result = await searchCEP(cep);
      if (result.success) {
        setValue("address", result.data.address);
        setValue("neighborhood", result.data.neighborhood);
        setValue("city", result.data.city);
        setValue("state", result.data.state);
        clearErrors(["address", "neighborhood", "city", "state"]);
      }
    } catch (error) {
      console.error("Erro ao buscar CEP:", error);
    } finally {
      setCepLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      // Validar documento baseado no tipo de pessoa
      if (data.person_type === "PF" && data.cpf) {
        if (!validateCPF(data.cpf)) {
          setNotify({
            title: "Erro!",
            message: "CPF inválido",
            type: "error",
            visible: true,
          });
          setLoading(false);
          return;
        }
      }

      if (data.person_type === "PJ" && data.cnpj) {
        if (!validateCNPJ(data.cnpj)) {
          setNotify({
            title: "Erro!",
            message: "CNPJ inválido",
            type: "error",
            visible: true,
          });
          setLoading(false);
          return;
        }
      }

      if(data?.phone_primary){
        data.phone_primary = formatPhoneForAPI(data.phone_primary);
      }
      if (data?.phone_secondary) {
        data.phone_secondary = formatPhoneForAPI(data.phone_secondary);
      }
      if (data?.emergency_contact_phone) {
        data.emergency_contact_phone = formatPhoneForAPI(
          data.emergency_contact_phone
        );
      }

      // Preparar dados para envio
      const preparedData = prepareCustomerData(data);

      // Converter tags para string
      if (data.tags_list && data.tags_list.length > 0) {
        preparedData.tags = data.tags_list.join(", ");
      }

      let response;
      if (isEdit) {
        response = await updateCustomer(customer.id, preparedData);
      } else {
        response = await createCustomer(preparedData);
      }

      setNotify({
        title: "Sucesso!",
        message: `Cliente ${isEdit ? "atualizado" : "criado"} com sucesso!`,
        type: "success",
        visible: true,
      });
      setTimeout(() => {
        window.location.href = "/customers";
      }, 2500);
      onSave();
      handleClose();
     
    } catch (error) {
      console.error("Erro ao salvar cliente:", error);

      let errorMessage = "Erro ao salvar cliente";
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === "object") {
          errorMessage = Object.values(errorData).flat().join(", ");
        } else {
          errorMessage = errorData.detail || errorData.message || errorMessage;
        }
      }

      setNotify({
        title: "Erro!",
        message: errorMessage,
        type: "error",
        visible: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const personTypeOptions = [
    { value: "PF", label: "Pessoa Física" },
    { value: "PJ", label: "Pessoa Jurídica" },
  ];

  const statusOptions = [
    { value: "active", label: "Ativo" },
    { value: "inactive", label: "Inativo" },
    { value: "blocked", label: "Bloqueado" },
    { value: "potential", label: "Potencial" },
  ];

  const customerTypeOptions = [
    { value: "new", label: "Novo" },
    { value: "regular", label: "Regular" },
    { value: "vip", label: "VIP" },
    { value: "returning", label: "Retornando" },
  ];

  const genderOptions = [
    { value: "M", label: "Masculino" },
    { value: "F", label: "Feminino" },
    { value: "O", label: "Outro" },
    { value: "N", label: "Não informado" },
  ];

  const maritalStatusOptions = [
    { value: "single", label: "Solteiro(a)" },
    { value: "married", label: "Casado(a)" },
    { value: "divorced", label: "Divorciado(a)" },
    { value: "widowed", label: "Viúvo(a)" },
    { value: "other", label: "Outro" },
  ];

  const acquisitionChannelOptions = [
    { value: "website", label: "Site/Website" },
    { value: "whatsapp", label: "WhatsApp" },
    { value: "instagram", label: "Instagram" },
    { value: "facebook", label: "Facebook" },
    { value: "google", label: "Google/Busca" },
    { value: "referral", label: "Indicação" },
    { value: "walk_in", label: "Entrada Direta" },
    { value: "advertising", label: "Publicidade" },
    { value: "other", label: "Outro" },
  ];

  const communicationPreferenceOptions = [
    { value: "whatsapp", label: "WhatsApp" },
    { value: "email", label: "E-mail" },
    { value: "sms", label: "SMS" },
    { value: "phone", label: "Telefone" },
    { value: "all", label: "Todos" },
  ];

  const handleClose = () => {
    reset();
    setActiveTab("basic");
    clearErrors();
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <IconUser className="me-2" />
          {isEdit ? "Editar Cliente" : "Novo Cliente"}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            {/* Aba Dados Básicos */}
            <Tab eventKey="basic" title="Dados Básicos">
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Nome Completo <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      {...register("full_name", {
                        required: "Nome completo é obrigatório",
                      })}
                      isInvalid={!!errors.full_name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.full_name?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Apelido/Nome Fantasia</Form.Label>
                    <Form.Control type="text" {...register("nickname")} />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Tipo de Pessoa <span className="text-danger">*</span>
                    </Form.Label>
                    <Controller
                      name="person_type"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={personTypeOptions}
                          value={personTypeOptions.find(
                            (opt) => opt.value === field.value
                          )}
                          onChange={(option) => field.onChange(option?.value)}
                          placeholder="Selecione..."
                        />
                      )}
                    />
                  </Form.Group>
                </Col>

                {personType === "PF" && (
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        CPF <span className="text-danger">*</span>
                      </Form.Label>
                      <Controller
                        name="cpf"
                        control={control}
                        rules={{
                          required:
                            personType === "PF" ? "CPF é obrigatório" : false,
                        }}
                        render={({ field }) => (
                          <NumberFormat
                            {...field}
                            format="###.###.###-##"
                            mask="_"
                            className={`form-control ${
                              errors.cpf ? "is-invalid" : ""
                            }`}
                            placeholder="000.000.000-00"
                          />
                        )}
                      />
                      {errors.cpf && (
                        <div className="invalid-feedback d-block">
                          {errors.cpf.message}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                )}

                {personType === "PJ" && (
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        CNPJ <span className="text-danger">*</span>
                      </Form.Label>
                      <Controller
                        name="cnpj"
                        control={control}
                        rules={{
                          required:
                            personType === "PJ" ? "CNPJ é obrigatório" : false,
                        }}
                        render={({ field }) => (
                          <NumberFormat
                            {...field}
                            format="##.###.###/####-##"
                            mask="_"
                            className={`form-control ${
                              errors.cnpj ? "is-invalid" : ""
                            }`}
                            placeholder="00.000.000/0000-00"
                          />
                        )}
                      />
                      {errors.cnpj && (
                        <div className="invalid-feedback d-block">
                          {errors.cnpj.message}
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                )}

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>RG</Form.Label>
                    <Form.Control type="text" {...register("rg")} />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Telefone Principal <span className="text-danger">*</span>
                    </Form.Label>
                    <Controller
                      name="phone_primary"
                      control={control}
                      rules={{
                        required: "Telefone principal é obrigatório",
                      }}
                      render={({ field }) => (
                        <NumberFormat
                          {...field}
                          format="(##) #####-####"
                          mask="_"
                          className={`form-control ${
                            errors.phone_primary ? "is-invalid" : ""
                          }`}
                          placeholder="(00) 00000-0000"
                        />
                      )}
                    />
                    {errors.phone_primary && (
                      <div className="invalid-feedback d-block">
                        {errors.phone_primary.message}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Telefone Secundário</Form.Label>
                    <Controller
                      name="phone_secondary"
                      control={control}
                      render={({ field }) => (
                        <NumberFormat
                          {...field}
                          format="(##) #####-####"
                          mask="_"
                          className="form-control"
                          placeholder="(00) 00000-0000"
                        />
                      )}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>E-mail</Form.Label>
                    <Form.Control
                      type="email"
                      {...register("email", {
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "E-mail inválido",
                        },
                      })}
                      isInvalid={!!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.email?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Data de Nascimento</Form.Label>
                    <Form.Control type="date" {...register("birth_date")} />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Gênero</Form.Label>
                    <Controller
                      name="gender"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={genderOptions}
                          value={genderOptions.find(
                            (opt) => opt.value === field.value
                          )}
                          onChange={(option) => field.onChange(option?.value)}
                          placeholder="Selecione..."
                          styles={{
                            control: (provided) => ({
                              ...provided,
                              minHeight: "56px",
                              borderRadius: "1.25rem",
                            }),
                          }}
                        />
                      )}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Tab>

            {/* Aba Endereço */}
            <Tab eventKey="address" title="Endereço">
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>CEP</Form.Label>
                    <div className="input-group">
                      <Controller
                        name="zip_code"
                        control={control}
                        render={({ field }) => (
                          <NumberFormat
                            {...field}
                            format="#####-###"
                            mask="_"
                            className="form-control"
                            placeholder="00000-000"
                          />
                        )}
                      />
                      {cepLoading && (
                        <div className="input-group-text">
                          <Spinner animation="border" size="sm" />
                        </div>
                      )}
                    </div>
                  </Form.Group>
                </Col>
                <Col md={7}>
                  <Form.Group className="mb-3">
                    <Form.Label>Endereço</Form.Label>
                    <Form.Control type="text" {...register("address")} />
                  </Form.Group>
                </Col>
                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Número</Form.Label>
                    <Form.Control type="text" {...register("address_number")} />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Complemento</Form.Label>
                    <Form.Control
                      type="text"
                      {...register("address_complement")}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Bairro</Form.Label>
                    <Form.Control type="text" {...register("neighborhood")} />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Cidade</Form.Label>
                    <Form.Control type="text" {...register("city")} />
                  </Form.Group>
                </Col>
                <Col md={1}>
                  <Form.Group className="mb-3">
                    <Form.Label>UF</Form.Label>
                    <Form.Control
                      type="text"
                      {...register("state")}
                      maxLength={2}
                      style={{ textTransform: "uppercase" }}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Tab>

            {/* Aba Informações Comerciais */}
            <Tab eventKey="commercial" title="Informações Comerciais">
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={statusOptions}
                          value={statusOptions.find(
                            (opt) => opt.value === field.value
                          )}
                          onChange={(option) => field.onChange(option?.value)}
                          placeholder="Selecione..."
                        />
                      )}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tipo de Cliente</Form.Label>
                    <Controller
                      name="customer_type"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={customerTypeOptions}
                          value={customerTypeOptions.find(
                            (opt) => opt.value === field.value
                          )}
                          onChange={(option) => field.onChange(option?.value)}
                          placeholder="Selecione..."
                        />
                      )}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Canal de Aquisição</Form.Label>
                    <Controller
                      name="acquisition_channel"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={acquisitionChannelOptions}
                          value={acquisitionChannelOptions.find(
                            (opt) => opt.value === field.value
                          )}
                          onChange={(option) => field.onChange(option?.value)}
                          placeholder="Selecione..."
                          isClearable
                        />
                      )}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Estado Civil</Form.Label>
                    <Controller
                      name="marital_status"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={maritalStatusOptions}
                          value={maritalStatusOptions.find(
                            (opt) => opt.value === field.value
                          )}
                          onChange={(option) => field.onChange(option?.value)}
                          placeholder="Selecione..."
                          isClearable
                        />
                      )}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fonte da Indicação</Form.Label>
                    <Form.Control
                      type="text"
                      {...register("referral_source")}
                      placeholder="Nome de quem indicou ou fonte específica"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Preferência de Comunicação</Form.Label>
                    <Controller
                      name="communication_preference"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={communicationPreferenceOptions}
                          value={communicationPreferenceOptions.find(
                            (opt) => opt.value === field.value
                          )}
                          onChange={(option) => field.onChange(option?.value)}
                          placeholder="Selecione..."
                        />
                      )}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Horário Preferencial</Form.Label>
                    <Form.Control
                      type="text"
                      {...register("preferred_schedule")}
                      placeholder="Ex: Manhã, Tarde, Noite, Fins de semana"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tags</Form.Label>
                    <Controller
                      name="tags_list"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={tags}
                          value={tags.filter((tag) =>
                            field.value?.includes(tag.label)
                          )}
                          onChange={(selectedOptions) => {
                            const values =
                              selectedOptions?.map((opt) => opt.label) || [];
                            field.onChange(values);
                          }}
                          placeholder="Selecione tags..."
                          isMulti
                          isClearable
                        />
                      )}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Observações</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      {...register("notes")}
                      placeholder="Observações gerais sobre o cliente"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="checkbox"
                      id="accepts_marketing"
                      label="Aceita receber campanhas de marketing"
                      {...register("accepts_marketing")}
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="checkbox"
                      id="is_whatsapp_active"
                      label="WhatsApp ativo"
                      {...register("is_whatsapp_active")}
                    />
                  </div>
                </Col>
              </Row>
            </Tab>

            {/* Aba Contato de Emergência */}
            <Tab eventKey="emergency" title="Contato de Emergência">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nome do Contato de Emergência</Form.Label>
                    <Form.Control
                      type="text"
                      {...register("emergency_contact_name")}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Telefone do Contato de Emergência</Form.Label>
                    <Controller
                      name="emergency_contact_phone"
                      control={control}
                      render={({ field }) => (
                        <NumberFormat
                          {...field}
                          format="(##) #####-####"
                          mask="_"
                          className="form-control"
                          placeholder="(00) 00000-0000"
                        />
                      )}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Tab>
          </Tabs>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={loading}
            className="d-flex align-items-center"
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Salvando...
              </>
            ) : (
              <>
                <IconCheck size={18} className="me-1" />
                {isEdit ? "Atualizar" : "Criar"} Cliente
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CustomerModal;
